import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, CheckCircle2, ChevronDown, Copy, Plus, Unlink } from 'lucide-react';
import { ApprovalState, socialPlatformLabel, type SocialConnection } from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { useDialogs } from '../../components/ui/DialogProvider';
import { Button } from '../../components/ui/Button';
import { CopyToDestinationsModal } from './CopyToDestinationsModal';
import { rememberCopiedText } from './copiedText';
import { cn } from '../../lib/cn';
import { hcUnderlineIdle, hcUnderlineSelected } from '../../lib/forcedColors';

/**
 * Which destination tab gets FOCUS for a key pressed on the row, or null when the key is not one of
 * the row's own: Left and Right step and wrap, Home and End go to the ends.
 *
 * Focus only. Choosing a destination opens another post, and what is unsaved in this one does not
 * survive that - so a tab is chosen with Enter or Space (its own click), never with an arrow key.
 */
export function focusIndexAfterKey(count: number, current: number, key: string): number | null {
  if (count <= 0) return null;
  switch (key) {
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/**
 * The destinations of one idea, as tabs above the composer.
 *
 * A standalone post shows only "Add destination" — which is how a set comes to exist. Once there are
 * two, the tabs appear and switching between them is a `?compose=` change: opening a different post
 * is already what the app does when you click another row in the list, so a destination tab is that
 * same transition with a shorter path. It inherits that behaviour, including the fact that unsaved
 * edits do not survive it, rather than inventing a second kind of navigation with its own hazards.
 */
export function DestinationTabs({
  part,
  postId,
  groupId,
  currentConnectionId,
  currentPlatform,
  onSelect,
  ensurePostId,
  addDisabledReason,
}: {
  /**
   * Which half of this to draw.
   *
   * The two live in different places on the page — the tab strip at the top of the modal, the
   * "add" control down beside the Type dropdown where the rest of the post's setup is — so the
   * component is rendered twice rather than portalling one half across the tree. Both instances
   * read the same react-query caches, so the second costs no extra request, and the only state
   * that matters (which accounts are being chosen) belongs to the 'actions' instance alone.
   */
  part: 'tabs' | 'actions';
  /**
   * Why adding a destination is not available yet, or null when it is. A destination is a sibling
   * POST, so it needs somewhere to live before it can exist.
   */
  addDisabledReason?: string | null;
  /** The destination currently open, or null on a post that has not been saved yet. */
  postId: string | null;
  /** The set it belongs to, or null when it stands alone. */
  groupId: string | null;
  /**
   * Where the open post publishes. Needed even with no set yet: otherwise the picker offers this
   * post's OWN account as a new destination, and the only possible outcome is a rejected request.
   */
  currentConnectionId: string | null;
  currentPlatform: string | null;
  onSelect: (siblingPostId: string) => void;
  /**
   * Save the post being composed and return its id.
   *
   * A destination is seeded FROM an existing post — it copies that post's words and media — so a
   * brand-new post has to become real before it can have a second one. Rather than hold pending
   * destinations in memory and reconcile them at save time (two ways to create the same thing, and
   * a half-made set whenever the second half fails), adding one on a new post saves it first. The
   * composer then holds a saved post with real siblings, which is the state every other control
   * here is written for.
   */
  ensurePostId?: () => Promise<string>;
}) {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const { alert, confirm } = useDialogs();
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [copying, setCopying] = useState(false);

  const group = useQuery({
    queryKey: ['post-group', tenantKey, groupId],
    queryFn: ({ signal }) => api.getPostGroup(groupId as string, signal),
    enabled: Boolean(groupId) && tenantKey !== '',
  });
  const connections = useQuery({
    queryKey: ['social-connections', tenantKey],
    queryFn: ({ signal }) => api.listSocialConnections(signal),
    enabled: tenantKey !== '',
  });

  const destinations = useMemo(() => group.data?.destinations ?? [], [group.data]);

  // ── the row of tabs, by keyboard ──
  // One Tab stop for the whole row (a roving tabindex): the arrow keys, Home and End move focus
  // inside it, and only that (focusIndexAfterKey). It starts on the open destination's tab, and
  // goes back to it whenever the open post or the set changes.
  const activeIndex = destinations.findIndex((d) => d.postId === postId);
  const [focusIndex, setFocusIndex] = useState(activeIndex);
  useEffect(() => setFocusIndex(activeIndex), [activeIndex, destinations.length]);
  const roving =
    focusIndex >= 0 && focusIndex < destinations.length ? focusIndex : Math.max(activeIndex, 0);
  const tabRefs = useRef(new Map<number, HTMLButtonElement>());
  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // The browser's own: Alt+Left is Back.
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    // From a tab only: the two buttons at the end of the row keep their keys.
    if (!(e.target instanceof HTMLElement) || e.target.getAttribute('role') !== 'tab') return;
    const next = focusIndexAfterKey(destinations.length, roving, e.key);
    if (next == null) return;
    e.preventDefault();
    setFocusIndex(next);
    tabRefs.current.get(next)?.focus();
  };

  /**
   * Siblings whose approval an edit here would revoke.
   *
   * Divergence cannot be measured — two versions can read alike and still miss the intent — so an
   * edit to one destination sends the rest back for review rather than trying to judge whether the
   * set still holds together. Said before the edit, because an approval quietly disappearing is the
   * kind of thing someone finds out about from an approver.
   */
  const wouldReopen = useMemo(
    () =>
      destinations.filter(
        (d) =>
          d.postId !== postId &&
          !d.publishedAt &&
          d.approvalState !== null &&
          d.approvalState !== ApprovalState.DRAFT &&
          d.approvalState !== ApprovalState.REVISE &&
          d.approvalState !== ApprovalState.REJECTED,
      ),
    [destinations, postId],
  );
  /** Already out — an edit here cannot recall them, and nobody should think it did. */
  const alreadyOut = destinations.filter((d) => d.postId !== postId && d.publishedAt);

  /**
   * Accounts not already in the set.
   *
   * Keyed on platform + account name because that is all a destination carries here; offering one
   * that is already taken would produce a button whose only possible outcome is an error.
   */
  const available = useMemo(() => {
    const used = new Set(destinations.map((d) => `${d.platform ?? ''}:${d.accountName ?? ''}`));
    return (connections.data ?? [])
      .filter((c): c is SocialConnection => c.connected && Boolean(c.id))
      // The open post's own destination, which `used` cannot know about while there is no set yet.
      // Two forms: it has an account, or it is still just aimed at a platform — and the server
      // treats two accountless posts for one platform as the same destination, so both are excluded.
      .filter((c) =>
        currentConnectionId ? c.id !== currentConnectionId : c.platform !== currentPlatform,
      )
      .filter((c) => !used.has(`${c.platform}:${c.accountName ?? ''}`));
  }, [connections.data, destinations, currentConnectionId, currentPlatform]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['post-group', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['post', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
  };

  const seed = useMutation({
    mutationFn: async (connectionIds: string[]) => {
      // The post first, then its destinations: seeding COPIES a post, so there has to be one.
      const id = postId ?? (await ensurePostId?.());
      if (!id) throw new Error('Save this post before adding another destination.');
      // The posts the set holds already, so that the one each seed adds can be told from them.
      const known = new Set([id, ...destinations.map((d) => d.postId)]);
      // Sequentially, not in parallel: each seed reads the source post and writes a sibling into
      // the same set, and the server assigns the group on the first one. Firing them together
      // races that assignment and can leave one idea holding two sets.
      for (const connectionId of connectionIds) {
        const set = await api.seedDestination(id, connectionId);
        // The new destination's words were copied from this post, written for this post's
        // platform: on another platform it opens asking for a rewrite until it is saved
        // (copiedText.ts). It is the post the set did not hold before, on the chosen ACCOUNT -
        // matched by platform and account name, the key `available` uses; a set holds one post
        // per account. The platform alone is not enough: while the set's list is still on its
        // way, or failed, `known` holds this post only, and a second account on a platform the set
        // already holds would mark that platform's own post - the Content Strategist's, say - as
        // copied, though nobody touched it.
        const from = set.destinations.find((d) => d.postId === id)?.platform ?? null;
        const account = connections.data?.find((c) => c.id === connectionId) ?? null;
        const to = account?.platform ?? null;
        for (const d of set.destinations) {
          if (known.has(d.postId)) continue;
          known.add(d.postId);
          if (
            from &&
            d.platform === to &&
            (d.accountName ?? '') === (account?.accountName ?? '') &&
            d.platform !== from
          ) {
            rememberCopiedText(tenantKey, d.postId, from);
          }
        }
      }
    },
    onSuccess: () => {
      setAdding(false);
      setChosen([]);
      refresh();
    },
    onError: (err) =>
      void alert({
        title: 'Could not add that destination',
        message: actionErrorText(err, 'Please try again.'),
      }),
  });

  const detach = useMutation({
    // Reachable only from the tab strip, which requires a saved post in a set of two or more.
    mutationFn: () => api.detachDestination(groupId as string, postId as string),
    onSuccess: refresh,
    onError: (err) =>
      void alert({
        title: 'Could not remove that destination',
        message: actionErrorText(err, 'Please try again.'),
      }),
  });

  const askDetach = async () => {
    const ok = await confirm({
      title: 'Remove this destination from the set?',
      // Spelled out because "remove" reads as "delete", and this is the one control on this strip a
      // person could reasonably expect to destroy something.
      message:
        'The post is kept. It stops being one of this idea’s destinations and keeps its own content, schedule and approval.',
      confirmLabel: 'Remove from set',
    });
    if (ok) detach.mutate();
  };

  const adder = (
    <AddDestination
      available={available}
      chosen={chosen}
      setChosen={setChosen}
      onAdd={() => seed.mutate(chosen)}
      pending={seed.isPending}
      adding={adding}
      setAdding={setAdding}
      disabledReason={addDisabledReason ?? null}
    />
  );

  // The "add" control, wherever the composer has put it — next to the Type dropdown, with the rest
  // of what decides where this post goes. It is the same control on a standalone post and on a
  // set, which is why it does not depend on `destinations` at all.
  if (part === 'actions') {
    if (available.length === 0) return null;
    return adder;
  }

  // A standalone post, or a set of one: no tabs at all — a tab strip with a single tab is a
  // control that does nothing, and the way to ADD a second destination now lives in the 'actions'
  // part beside the Type dropdown.
  if (destinations.length < 2) return null;

  return (
    <div className="mb-4 border-b border-line-soft">
      <div
        className="flex flex-wrap items-end gap-1"
        role="tablist"
        aria-label="Destinations"
        onKeyDown={onTabKeyDown}
      >
        {destinations.map((d, i) => {
          const active = d.postId === postId;
          const label = d.platform ? socialPlatformLabel[d.platform] : 'Destination';
          return (
            <button
              key={d.postId}
              type="button"
              role="tab"
              aria-selected={active}
              ref={(el) => {
                if (el) tabRefs.current.set(i, el);
                else tabRefs.current.delete(i);
              }}
              tabIndex={i === roving ? 0 : -1}
              onFocus={() => setFocusIndex(i)}
              onClick={() => !active && onSelect(d.postId)}
              className={cn(
                // min-h-11: 44px to tap, on every device, as the Preview / AI assist tabs are.
                'focus-ring -mb-px flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-sm',
                // Forced colours (Windows High Contrast) paint every underline alike: the open
                // destination's takes a system colour, and the others' are painted out.
                active
                  ? cn('border-action font-semibold text-ink', hcUnderlineSelected)
                  : cn('border-transparent text-ink-muted hover:text-ink', hcUnderlineIdle),
              )}
            >
              <span>{label}</span>
              {/* The account, when there is one — two LinkedIn pages are otherwise identical tabs. */}
              {d.accountName && (
                <span className="max-w-[10rem] truncate text-xs font-normal text-ink-faint">
                  {d.accountName}
                </span>
              )}
              {/* Where this destination stands. Each one publishes on its own, so a set where one
                  went out and one didn't is the normal case rather than an edge one. */}
              {d.publishError ? (
                <AlertCircle className="h-3.5 w-3.5 text-red-500 dark:text-error-dark" aria-label="Failed" />
              ) : d.publishedAt ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-success dark:text-success-dark" aria-label="Published" />
              ) : null}
            </button>
          );
        })}

        {/* Copy-to and remove-from-set stay with the tabs: they act on THIS set, which is what
            the strip above them is. Only the adder moved down beside Type. */}
        <div className="ml-auto flex items-center gap-2 pb-1">
          {/* 44px to tap on a touch screen; with a mouse the row keeps its height. */}
          <Button
            variant="secondary"
            size="sm"
            className="[@media(pointer:coarse)]:min-h-11"
            onClick={() => setCopying(true)}
          >
            <Copy className="h-4 w-4" aria-hidden />
            Copy to&hellip;
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="[@media(pointer:coarse)]:min-h-11"
            loading={detach.isPending}
            onClick={() => void askDetach()}
          >
            <Unlink className="h-4 w-4" aria-hidden />
            Remove from set
          </Button>
        </div>
      </div>

      {(wouldReopen.length > 0 || alreadyOut.length > 0) && (
        <div className="mt-3 space-y-1.5">
          {wouldReopen.length > 0 && (
            <p className="rounded-md bg-amber-50 dark:bg-warning-dark-tint px-2.5 py-1.5 text-xs text-amber-800 dark:text-warning-dark">
              Editing this destination sends{' '}
              {wouldReopen.length === 1
                ? 'the other destination'
                : `the other ${wouldReopen.length} destinations`}{' '}
              back for review — whether the set still says one thing is a judgement, not something we
              can measure.
            </p>
          )}
          {alreadyOut.length > 0 && (
            <p className="rounded-md bg-page px-2.5 py-1.5 text-xs text-ink-label">
              {alreadyOut.length === 1 ? 'One destination has' : `${alreadyOut.length} destinations have`}{' '}
              already published. Editing here does not change what went out.
            </p>
          )}
        </div>
      )}

      <CopyToDestinationsModal
        open={copying}
        onClose={() => setCopying(false)}
        fromPostId={postId as string}
        destinations={destinations}
        onCopied={refresh}
      />
    </div>
  );
}

/**
 * Add another destination, seeded from the post currently open.
 *
 * It copies this post's words and media to a new post aimed at the chosen account, in one call —
 * duplicate-then-attach would leave a stray copy loose in the campaign whenever the second half
 * failed. Renders nothing when every connected account is already a destination.
 */
function AddDestination({
  available,
  chosen,
  setChosen,
  onAdd,
  pending,
  adding,
  setAdding,
  disabledReason,
}: {
  available: SocialConnection[];
  chosen: string[];
  setChosen: (v: string[]) => void;
  onAdd: () => void;
  pending: boolean;
  adding: boolean;
  setAdding: (v: boolean) => void;
  /** Why this cannot be used yet, or null. Doubles as the button's tooltip. */
  disabledReason: string | null;
}) {
  if (available.length === 0) return null;
  if (!adding) {
    return (
      // flex-wrap: where the reason does not fit beside the button it goes under it. Squeezed in
      // beside it, it took the button's width and broke the label onto two lines.
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={Boolean(disabledReason)}
          onClick={() => setAdding(true)}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add destination
        </Button>
        {/* Beside the button rather than in its tooltip: a disabled control that explains itself
            only on hover is one most people never get an explanation from at all. */}
        {disabledReason && (
          <span className="text-sm text-ink-muted" role="note">
            {disabledReason}
          </span>
        )}
      </div>
    );
  }
  const toggle = (id: string) =>
    setChosen(chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id]);

  return (
    // flex-wrap: the account picker, Add and Cancel are wider than a phone; the buttons drop under
    // the picker there.
    <div className="flex flex-wrap items-start gap-2">
      <AccountMultiSelect available={available} chosen={chosen} onToggle={toggle} />
      <Button size="sm" disabled={chosen.length === 0} loading={pending} onClick={onAdd}>
        {chosen.length > 1 ? "Add " + chosen.length : "Add"}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setAdding(false);
          setChosen([]);
        }}
      >
        Cancel
      </Button>
    </div>
  );
}

/**
 * Pick SEVERAL accounts at once.
 *
 * A designed listbox rather than a native `multiple` select, for the reason every other dropdown in
 * this app is one: the native control needs ctrl/cmd-click to take a second option, a convention
 * most people neither know nor can discover. Checkboxes say what they do.
 */
function AccountMultiSelect({
  available,
  chosen,
  onToggle,
}: {
  available: SocialConnection[];
  chosen: string[];
  onToggle: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const summary =
    chosen.length === 0
      ? "Choose accounts..."
      : chosen.length === 1
        ? labelFor(available.find((c) => c.id === chosen[0]))
        : chosen.length + " accounts selected";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="focus-ring flex w-56 items-center justify-between gap-2 rounded-lg border border-line-input bg-card px-3 py-2 text-left text-sm text-ink hover:border-ink-ghost"
      >
        <span className={cn("truncate", chosen.length === 0 && "text-ink-faint")}>{summary}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
      </button>
      {open && (
        <>
          {/* Click-away. A transparent overlay rather than a document listener: the panel sits
              inside a modal that already stops propagation in places, and an overlay cannot be
              outrun by a handler that swallows the event first. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <ul
            role="listbox"
            aria-multiselectable
            aria-label="Accounts for the new destinations"
            className="absolute z-20 mt-1 max-h-64 w-72 overflow-y-auto rounded-lg border border-line bg-card py-1 shadow-panel"
          >
            {available.map((c) => {
              const id = c.id as string;
              const picked = chosen.includes(id);
              return (
                <li key={id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={picked}
                    onClick={() => onToggle(id)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-hover"
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                        picked ? "border-action bg-action text-action-fg" : "border-line-input",
                      )}
                    >
                      {picked && <Check className="h-3 w-3" aria-hidden />}
                    </span>
                    <span className="truncate">{labelFor(c)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function labelFor(c: SocialConnection | undefined): string {
  if (!c) return "Account";
  return socialPlatformLabel[c.platform] + (c.accountName ? " - " + c.accountName : "");
}
